import { Paper as MantinePaper, PaperProps, useMantineColorScheme, useMantineTheme, Transition, Box } from '@mantine/core';
import { forwardRef, useMemo } from 'react';
import { useReducedMotion } from '@mantine/hooks';
import { getContrastingTextColor } from '../../../utils/color';
import { usePreferencesStore } from '../../../stores';
import { LINE_HEIGHT_MAP, LETTER_SPACING_MAP } from '../../../hooks/useAccessibilityStyles';

export type GradientVariant =
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'success'
  | 'info'
  | 'warning'
  | 'danger';

export type PaperVariant = 'default' | 'elevated' | 'outlined' | 'subtle' | 'gradient' | 'glass';

export interface ThemedPaperProps extends PaperProps {
  variant?: PaperVariant;
  gradientVariant?: GradientVariant;
  gradientOpacity?: number;
  motion?: { type?: 'fade' | 'slide-up' | 'scale'; duration?: number; delay?: number };
  /** Children content */
  children?: React.ReactNode;
  /** Click handler */
  onClick?: React.MouseEventHandler<HTMLDivElement>;
  /** Mouse enter handler */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>;
  /** Mouse leave handler */
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>;
  /** Drag start handler */
  onDragStart?: React.DragEventHandler<HTMLDivElement>;
  /** Drag end handler */
  onDragEnd?: React.DragEventHandler<HTMLDivElement>;
  /** Drag over handler */
  onDragOver?: React.DragEventHandler<HTMLDivElement>;
  /** Drop handler */
  onDrop?: React.DragEventHandler<HTMLDivElement>;
  /** Whether element is draggable */
  draggable?: boolean;
}

const Paper = forwardRef<HTMLDivElement, ThemedPaperProps>(
  ({ variant = 'default', gradientVariant = 'primary', gradientOpacity = 0.1, motion, children, style, ...rest }, ref) => {
    const { colorScheme } = useMantineColorScheme();
    const theme = useMantineTheme();
    const isDark = colorScheme === 'dark';
    const reducedMotion = useReducedMotion();
    const lineSpacing = usePreferencesStore((state) => state.lineSpacing);
    const letterSpacingPref = usePreferencesStore((state) => state.letterSpacing);

    const accessibilityStyles = {
      lineHeight: LINE_HEIGHT_MAP[lineSpacing],
      letterSpacing: LETTER_SPACING_MAP[letterSpacingPref],
    };

    const gradient = theme.other.gradients[gradientVariant];

    // Calculate optimal text color for full opacity gradient background
    const gradientBg = `linear-gradient(135deg, ${gradient.from}, ${gradient.to})`;
    const autoTextColor = useMemo(
      () => getContrastingTextColor(gradientBg),
      [gradientBg]
    );

    const basePaper = (() => {
      if (variant === 'gradient') {
        const opacity = Math.round(gradientOpacity * 255)
          .toString(16)
          .padStart(2, '0');
        // Only apply auto text color for full or near-full opacity gradients
        const shouldApplyTextColor = gradientOpacity > 0.8;
        return (
          <MantinePaper
            ref={ref}
            radius="md"
            style={{
              backgroundImage: `linear-gradient(135deg, ${gradient.from}${opacity}, ${gradient.to}${opacity})`,
              border: `1px solid ${gradient.from}30`,
              color: shouldApplyTextColor ? (style?.color || autoTextColor) : undefined,
              ...accessibilityStyles,
              ...style,
            }}
            {...rest}
          >
            {children}
          </MantinePaper>
        );
      }

      if (variant === 'glass') {
        return (
          <MantinePaper
            ref={ref}
            radius="md"
            style={{
              backdropFilter: 'blur(10px)',
              backgroundColor: isDark ? 'rgba(0, 0, 0, 0.2)' : 'rgba(255, 255, 255, 0.6)',
              border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'}`,
              ...accessibilityStyles,
              ...style,
            }}
            {...rest}
          >
            {children}
          </MantinePaper>
        );
      }

      const variantStyles: Record<Exclude<PaperVariant, 'gradient' | 'glass'>, React.CSSProperties> = {
        default: {},
        elevated: {
          boxShadow: isDark
            ? '0 4px 20px rgba(0, 0, 0, 0.5)'
            : '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
        outlined: {
          border: `2px solid ${isDark ? theme.colors.dark[4] : theme.colors.gray[4]}`,
        },
        subtle: {
          backgroundColor: isDark ? theme.colors.dark[7] : theme.colors.gray[0],
          border: 'none',
        },
      };

      return (
        <MantinePaper
          ref={ref}
          radius="md"
          style={{
            ...variantStyles[variant],
            ...accessibilityStyles,
            ...style,
          }}
          {...rest}
        >
          {children}
        </MantinePaper>
      );
    })();

    if (motion) {
      return (
        <Transition
          mounted
          transition={motion.type || 'fade'}
          duration={reducedMotion ? 0 : motion.duration ?? 200}
          timingFunction="ease-out"
        >
          {(styles) => <Box style={{ ...styles, transitionDelay: `${motion.delay ?? 0}ms` }}>{basePaper}</Box>}
        </Transition>
      );
    }

    return basePaper;
  }
);

Paper.displayName = 'Paper';

export default Paper;
